import React, { useState, useMemo } from "react";
import { useFormikContext } from "formik";
import {
  electricalInfo,
  getInventoryItems,
  mechanicalInfo,
  plumbingInfo,
  reservations,
  shipments,
} from "../../../constants/fields";
import useAllFields from "../../../hooks/useAllFields";
import useAuth from "../../../hooks/useAuth";
import useFilterFields from "../../../hooks/useFilterFields";
import { useProject } from "../../../contexts/project";
import Box from "@mui/material/Box";
import useSettingsConfig from "../../../views/settings/hooks/useSettingsConfig";
import useHiddenFields from "../../../views/settings/hooks/useHiddenFields";
import useFields from "../../../views/settings/hooks/useFields";
import Table from "@mui/material/Table";
import FieldsTable from "./FieldsTable";
import FieldsTableHead from "./FieldsTableHead";
import TableContainer from "@mui/material/TableContainer";
import Paper from "@mui/material/Paper";
import AdvancedOptionsTable from "./AdvancedOptionsTable";
import ConfigurableFieldsTable from "./ConfigurableFieldsTable";
import ConfigurableFieldDialog from "../../../views/settings/ConfigurableFieldDialog";
import SearchField from "./SearchField";
import PdfFieldsSettings from "./PdfFieldsSettings";
import FieldOrderSettings from "./FieldOrderSettings";
import StoreCardFieldsSettings from "./StoreCardFieldsSettings";
import EditCheckoutDisplayFieldLabelDialog from "../../../views/settings/EditCheckoutDisplayFieldLabelDialog";
import { CHECKOUT_DISPLAY_EDITABLE_SETTING_KEYS_BY_FIELD } from "../../../constants/checkoutDisplayFieldLabels";

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    flexGrow: 1,
  },
  tableContainer: {
    maxHeight: "70vh",
    overflow: "auto",
  },
  screenLayoutTableContainer: {
    overflowX: "auto",
    overflowY: "visible",
    flexShrink: 0,
  },
  table: {
    fontSize: 16,
  },
};

export default function FieldsSettingsList({
  activeTab,
  ordersTableFieldsEnabled = true,
  reservationsTableFieldsEnabled = true,
  receivingWizardEnabled = false,
  storageEnabled = true,
  advancedOptionsEnabled = true,
  project,
  hideFields = [],
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [checkoutLabelEditor, setCheckoutLabelEditor] = useState(null);
  const { setFieldValue, submitForm, values } = useFormikContext();
  const { user } = useAuth();
  const currentProject = useProject();
  const configuredProject = project || currentProject;
  const { settings } = user.company;
  const mergedCompanySettings = useMemo(
    () => ({ ...user.company.settings, ...(values.settings || {}) }),
    [user.company.settings, values.settings]
  );
  const { identifiers, descriptiveInfo, originInfo, destinationInfo } =
    useAllFields(configuredProject);
  const inventoryItemsValues = getInventoryItems(settings);
  const combinedHideFields = useHiddenFields(hideFields);
  const settingsConfig = useSettingsConfig(combinedHideFields);
  const { inventoryItems, warehouseInfo, orders, inventoryRequest } = useFields(
    inventoryItemsValues,
    mergedCompanySettings
  );
  const medicalFieldsEnabled = settings.medical_fields;
  const onsiteInventoryCapture = configuredProject?.settings?.onsite_inventory_capture;
  const filteredIdentifiers = useFilterFields(identifiers, search);
  const filteredDescriptiveInfo = useFilterFields(descriptiveInfo, search);
  const filteredWarehouseInfo = useFilterFields(warehouseInfo, search);
  const filteredOriginInfo = useFilterFields(originInfo, search);
  const filteredDestinationInfo = useFilterFields(destinationInfo, search);
  const filteredElectricalInfo = useFilterFields(electricalInfo, search);
  const filteredPlumbingInfo = useFilterFields(plumbingInfo, search);
  const filteredMechanicalInfo = useFilterFields(mechanicalInfo, search);
  const filteredShipments = useFilterFields(shipments, search);
  const filteredInventoryItems = useFilterFields(inventoryItems, search);
  const filteredInventoryRequest = useFilterFields(inventoryRequest, search);
  const filteredOrders = useFilterFields(orders, search);
  const filteredReservations = useFilterFields(reservations, search);

  const checkoutDisplayRows = useMemo(
    () =>
      filteredInventoryRequest.map(([name, category, labelText]) => {
        const settingKey = CHECKOUT_DISPLAY_EDITABLE_SETTING_KEYS_BY_FIELD[name];
        if (!settingKey) {
          return [name, category, labelText];
        }
        const openEditor = () => setCheckoutLabelEditor({ settingKey, displayLabel: labelText });
        const cell = (
          <Box
            component="span"
            role="button"
            tabIndex={0}
            onKeyDown={e => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                openEditor();
              }
            }}
            onClick={openEditor}
            sx={{
              textDecoration: "underline",
              cursor: "pointer",
              "&:hover": { color: "primary.main" },
            }}
          >
            {labelText}
          </Box>
        );
        return [name, category, cell];
      }),
    [filteredInventoryRequest]
  );

  const handleAddNewField = () => {
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleSearchChange = value => {
    setSearch(value);
  };

  const handleConfigurableFieldCreated = async (customFieldTemplate, options = {}) => {
    const fieldKey = `custom_field_${customFieldTemplate.id}`;

    const updates = [
      setFieldValue(`fieldSettings.${fieldKey}`, true, false),
      setFieldValue(`receivingSettings.${fieldKey}`, true, false),
    ];

    if (options.saveImmediately) {
      updates.push(setFieldValue("keepOpenOnNextSubmit", true, false));
    }

    await Promise.all(updates);

    if (options.saveImmediately) {
      await submitForm();
      await setFieldValue("keepOpenOnNextSubmit", false, false);
    }
  };

  const enabledTemplateIds = useMemo(() => {
    const settings = [values?.fieldSettings, values?.receivingSettings].filter(Boolean);

    return [
      ...new Set(
        settings.flatMap(projectSettings =>
          Object.entries(projectSettings)
            .filter(([key, enabled]) => key.startsWith("custom_field_") && enabled)
            .map(([key]) => key.replace("custom_field_", ""))
        )
      ),
    ];
  }, [values?.fieldSettings, values?.receivingSettings]);

  const isProjectSpecificSettings = Boolean(configuredProject?.id);
  const originFieldsEnabled =
    !isProjectSpecificSettings ||
    !storageEnabled ||
    configuredProject?.settings?.onsite_capture_to_warehouse_storage === true;
  const destinationFieldsEnabled = !isProjectSpecificSettings || !storageEnabled;

  switch (activeTab) {
    case 1:
      return (
        <Box sx={styles.container}>
          <SearchField value={search} onChange={handleSearchChange} />
          <TableContainer component={Paper} sx={styles.tableContainer}>
            <Table size="small" stickyHeader sx={styles.table}>
              <FieldsTableHead displayReceivingSettings={receivingWizardEnabled} />
              <FieldsTable
                enabled={settingsConfig.includes("identifiers")}
                list={filteredIdentifiers}
                label="Identifiers"
                displayReceivingSettings={receivingWizardEnabled}
              />
              <FieldsTable
                enabled={settingsConfig.includes("descriptive_info")}
                list={filteredDescriptiveInfo}
                label="Descriptive"
                displayReceivingSettings={receivingWizardEnabled}
              />
              <FieldsTable
                enabled={settingsConfig.includes("warehouse_info") && !onsiteInventoryCapture}
                list={filteredWarehouseInfo}
                label="Storage"
                displayReceivingSettings={receivingWizardEnabled}
              />
              <FieldsTable
                enabled={originFieldsEnabled && settingsConfig.includes("origin_info")}
                list={filteredOriginInfo}
                label="Origin"
                displayReceivingSettings={receivingWizardEnabled}
              />
              <FieldsTable
                enabled={destinationFieldsEnabled && settingsConfig.includes("destination_info")}
                list={filteredDestinationInfo}
                label="Destination"
                displayReceivingSettings={receivingWizardEnabled}
              />
              <FieldsTable
                enabled={medicalFieldsEnabled && settingsConfig.includes("electrical_info")}
                list={filteredElectricalInfo}
                label="Electrical"
                displayReceivingSettings={receivingWizardEnabled}
              />
              <FieldsTable
                enabled={medicalFieldsEnabled && settingsConfig.includes("plumbing_info")}
                list={filteredPlumbingInfo}
                label="Plumbing"
                displayReceivingSettings={receivingWizardEnabled}
              />
              <FieldsTable
                enabled={medicalFieldsEnabled && settingsConfig.includes("mechanical_info")}
                list={filteredMechanicalInfo}
                label="Mechanical"
                displayReceivingSettings={receivingWizardEnabled}
              />
              <ConfigurableFieldsTable
                enabled={settings.user_configurable_custom_fields}
                displayReceivingSettings={receivingWizardEnabled}
                onAddNewField={handleAddNewField}
                search={search}
              />
            </Table>
          </TableContainer>
          {dialogOpen && (
            <ConfigurableFieldDialog
              onClose={handleDialogClose}
              onCreated={handleConfigurableFieldCreated}
              enabledTemplateIds={enabledTemplateIds}
            />
          )}
        </Box>
      );

    case 2:
      return (
        <Box sx={styles.container}>
          <SearchField value={search} onChange={handleSearchChange} />
          <TableContainer component={Paper} sx={styles.tableContainer}>
            <Table size="small" stickyHeader sx={styles.table}>
              <FieldsTable
                enabled={settingsConfig.includes("shipments")}
                list={filteredShipments}
                label="Receiving Assistant Display Fields"
                displayReceivingSettings={false}
              />
            </Table>
          </TableContainer>
        </Box>
      );

    case 3:
      return (
        <Box sx={{ ...styles.container, gap: 3 }}>
          <SearchField value={search} onChange={handleSearchChange} />
          <TableContainer component={Paper} sx={styles.screenLayoutTableContainer}>
            <Table size="small" stickyHeader sx={styles.table}>
              <FieldsTable
                enabled={settingsConfig.includes("inventory_request")}
                list={checkoutDisplayRows}
                label="Checkout Display Fields"
                displayReceivingSettings={false}
              />
              <FieldsTable
                enabled={!combinedHideFields.includes("orders") && ordersTableFieldsEnabled}
                list={filteredOrders}
                label="Orders Table Fields"
                displayReceivingSettings={false}
              />
              <FieldsTable
                enabled={
                  !combinedHideFields.includes("reservations") && reservationsTableFieldsEnabled
                }
                list={filteredReservations}
                label="Reservations Table Fields"
                displayReceivingSettings={false}
              />
            </Table>
          </TableContainer>
          <StoreCardFieldsSettings />
          {checkoutLabelEditor && (
            <EditCheckoutDisplayFieldLabelDialog
              open
              settingKey={checkoutLabelEditor.settingKey}
              initialLabel={
                values.settings?.[checkoutLabelEditor.settingKey] ??
                user.company.settings?.[checkoutLabelEditor.settingKey] ??
                checkoutLabelEditor.displayLabel
              }
              onClose={() => setCheckoutLabelEditor(null)}
              onSaved={(key, label) => setFieldValue(`settings.${key}`, label, false)}
            />
          )}
        </Box>
      );
    case 4:
      return <PdfFieldsSettings />;

    case 5:
      return <FieldOrderSettings />;

    default:
      return null;
  }
}
